'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { toast } from 'react-toastify';
import { useMediaCapture, type CapturedMedia } from '../../hooks/useMediaCapture';
import { InlineAlert } from '../inline-alert/inline-alert';
import { ScreenshotIcon, VideoIcon, StopIcon, Spinner } from './icons';
import { Camera } from "lucide-react";
import { FilledButton } from '../buttons/filled-button';
import { api } from '~/utils/api';
import { useForm, Controller, type SubmitHandler } from 'react-hook-form';
import { FeedbackSchema, type FeedbackType } from '@golf-district/shared/src/schema/feedback-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import Image from 'next/image';
import { useUserContext } from '~/contexts/UserContext';
import { useAppContext } from '~/contexts/AppContext';
import { useCourseContext } from '~/contexts/CourseContext';
import { useMediaQuery } from 'usehooks-ts';
import { useDraggableButton } from '~/hooks/useDraggableButton';
import { Close } from '../icons/close';
import { googleAnalyticsEvent } from '~/utils/googleAnalyticsUtils';

type SubmitStage = 'idle' | 'uploading' | 'saving';

function hideShareSnackbar(): () => void {
    const el = document.documentElement;
    const prev = el.style.marginBottom;
    el.style.marginBottom = '-80px';
    el.style.overflow = 'hidden';
    return () => {
        el.style.marginBottom = prev;
        el.style.overflow = '';
    };
}

// html2canvas v1 cannot parse CSS Color Level 4 functions (color, color-mix,
// oklch, oklab, lch, lab) and throws when it encounters them. We replace each
// occurrence with a safe fallback before rendering. Order matters: longer
// names go first so substring matches like `lab(` inside `oklab(` don't
// trigger early. The walk balances parens so nested var()/calc() inside the
// color function get consumed correctly.
const UNSUPPORTED_COLOR_FNS = ['color-mix', 'oklab', 'oklch', 'lab', 'lch', 'color'] as const;

function stripUnsupportedColorFunctions(css: string): string {
    let result = css;
    for (const fn of UNSUPPORTED_COLOR_FNS) {
        result = stripOne(result, fn);
    }
    return result;
}

function stripOne(css: string, fn: string): string {
    const needle = fn + '(';
    let out = '';
    let i = 0;
    while (i < css.length) {
        const idx = css.indexOf(needle, i);
        if (idx === -1) {
            out += css.slice(i);
            break;
        }
        const prev = idx > 0 ? css[idx - 1] : '';
        // Skip mid-identifier matches like `lab(` inside `oklab(` or
        // `--my-color(`; we only want standalone function calls.
        if (prev && /[A-Za-z0-9_-]/.test(prev)) {
            out += css.slice(i, idx + 1);
            i = idx + 1;
            continue;
        }
        out += css.slice(i, idx);
        let depth = 1;
        let j = idx + needle.length;
        while (j < css.length && depth > 0) {
            const c = css[j];
            if (c === '(') depth++;
            else if (c === ')') depth--;
            j++;
        }
        out += 'transparent';
        i = j;
    }
    return out;
}

export function FeedbackWidget() {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitStage, setSubmitStage] = useState<SubmitStage>('idle');
    const [serverError, setServerError] = useState<string | null>(null);

    const { user } = useUserContext();
    const { entity } = useAppContext();
    const { course } = useCourseContext();
    const isMobile = useMediaQuery('(max-width: 480px)');
    const isSmallMobile = useMediaQuery('(max-width: 360px)');

    const { ref: btnRef, pos: btnPos } = useDraggableButton();

    const {
        handleSubmit,
        setValue,
        control,
        formState: { errors }
    } = useForm<FeedbackType>({
        resolver: zodResolver(FeedbackSchema),
        defaultValues: { title: '', description: '', email: '', pageUrl: '', userAgent: '' },
    });

    useEffect(() => {
        setValue('pageUrl', window.location.href, { shouldValidate: true });
        setValue('userAgent', navigator.userAgent, { shouldValidate: true });
    }, [setValue]);

    useEffect(() => {
        if (user?.email) setValue('email', user.email);
    }, [user, setValue]);

    const {
        captureScreenshot,
        startRecording,
        stopRecording,
        isRecording,
        isCapturing,
        media,
        error: mediaError,
        addScreenshot,
        removeScreenshot,
        resetRecording,
        resetAll,
    } = useMediaCapture();

    const modalHidden = isCapturing;

    const getUploadUrlsMutation = api.feedback.getUploadUrls.useMutation();
    const submitFeedbackMutation = api.feedback.submitFeedback.useMutation();
    const firstInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open && !modalHidden) setTimeout(() => firstInputRef.current?.focus(), 50);
    }, [open, modalHidden]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !isCapturing) handleClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isCapturing]);

    const handleClose = useCallback(() => {
        setOpen(false);
        setServerError(null);
        setSubmitStage('idle');
        resetAll();
    }, [resetAll]);

    const handleCloseForRecording = useCallback(() => {
        setOpen(false);
        setServerError(null);
        setSubmitStage('idle');
    }, []);

    async function uploadBlob(uploadUrl: string, blob: Blob) {
        const res = await fetch(uploadUrl, {
            method: 'PUT',
            body: blob,
            headers: { 'Content-Type': blob.type },
        });
        if (!res.ok) throw new Error('S3 upload failed');
    }

    console.log('errors: ', errors);

    const toggleWidget = () => {
        setOpen((o) => {
            if (!o) googleAnalyticsEvent({ action: "btn_feedback_open" });
            return !o;
        });
    };

    const onSubmit: SubmitHandler<FeedbackType> = async (data) => {
        googleAnalyticsEvent({ action: "btn_feedback_submit" });
        setSubmitting(true);
        setServerError(null);
        try {
            setSubmitStage('uploading');
            const urls = await getUploadUrlsMutation.mutateAsync({
                screenshots: media.screenshots.map((_, i) => ({
                    contentType: 'image/png' as const,
                    fileName: `screenshot-${i + 1}.png`,
                })),
                recording: media.recording
                    ? { contentType: 'video/webm' as const, fileName: 'recording.webm' }
                    : null,
            });

            await Promise.all([
                ...media.screenshots.map((s, i) =>
                    urls.screenshots[i]
                        ? uploadBlob(urls.screenshots[i]!.uploadUrl, s.blob)
                        : Promise.resolve()
                ),
                media.recording && urls.recording
                    ? uploadBlob(urls.recording.uploadUrl, media.recording.blob)
                    : Promise.resolve(),
            ]);

            setSubmitStage('saving');
            const result = await submitFeedbackMutation.mutateAsync({
                title: data.title,
                description: data.description,
                email: data.email,
                screenshots: urls.screenshots.map((u, i) => ({
                    key: u.key,
                    sizeBytes: media.screenshots[i]!.blob.size,
                })),
                recordingKey: urls.recording?.key ?? null,
                recordingSizeBytes: media.recording?.blob.size ?? null,
                pageUrl: window.location.href,
                userAgent: navigator.userAgent,
                customerName: user?.name ?? undefined,
                color1: entity?.color1 ?? undefined,
                courseId: course?.id ?? undefined,
            });

            setValue('title', '', { shouldValidate: true });
            setValue('description', '', { shouldValidate: true });
            toast.success(`Thanks for your feedback! Ticket ${result.id} created.`, {
                progressStyle: { background: entity?.color1 ?? 'var(--primary-color)' },
            });
            handleClose();
        } catch (err) {
            setServerError(err instanceof Error ? err.message : 'Something went wrong.');
        } finally {
            setSubmitting(false);
            setSubmitStage('idle');
        }
    };

    async function handleCaptureScreenshot() {
        try {
            let blob: Blob | null = null;

            if (isMobile || isSmallMobile) {
                setOpen(false);

                const savedScrollX = window.scrollX;
                const savedScrollY = window.scrollY;

                window.scrollTo(0, 0);

                await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(() => r(null))));

                const pageHeight = Math.max(
                    document.body.scrollHeight,
                    document.documentElement.scrollHeight,
                    document.body.offsetHeight,
                    document.documentElement.offsetHeight,
                );

                // Cap to stay safely under iOS Safari's ~16M-pixel canvas limit.
                const captureHeight = Math.min(pageHeight, 8000);

                const canvas = await html2canvas(document.body, {
                    width: window.innerWidth,
                    height: captureHeight,

                    scrollX: 0,
                    scrollY: 0,
                    windowWidth: window.innerWidth,
                    windowHeight: window.innerHeight,

                    // scale: 1 prevents devicePixelRatio (often 3 on iPhones)
                    // from blowing past Safari's canvas memory limit.
                    scale: 1,

                    useCORS: true,
                    backgroundColor: '#ffffff',
                    removeContainer: true,
                    logging: false,

                    // Skip the floating feedback button so it doesn't appear
                    // in the captured image.
                    ignoreElements: (el) =>
                        (el as HTMLElement).dataset?.feedbackWidget === 'true',

                    // Strip CSS Color Level 4 functions from the cloned doc;
                    // html2canvas v1 throws on color()/color-mix()/oklch()/etc.
                    onclone: (clonedDoc) => {
                        clonedDoc.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
                            const style = el.getAttribute('style');
                            if (!style) return;
                            const stripped = stripUnsupportedColorFunctions(style);
                            if (stripped !== style) el.setAttribute('style', stripped);
                        });

                        Array.from(clonedDoc.styleSheets).forEach((sheet) => {
                            try {
                                const rules = sheet.cssRules;
                                if (!rules) return;
                                Array.from(rules).forEach((rule) => {
                                    if (rule instanceof CSSStyleRule) {
                                        const cssText = rule.style.cssText;
                                        const stripped = stripUnsupportedColorFunctions(cssText);
                                        if (stripped !== cssText) rule.style.cssText = stripped;
                                    }
                                });
                            } catch {
                                // Cross-origin stylesheets throw on access; safe to skip.
                            }
                        });
                    },
                });

                blob = await new Promise<Blob | null>((resolve) =>
                    canvas.toBlob(resolve, 'image/png', 1)
                );

                // Restore scroll position and reopen modal
                window.scrollTo(savedScrollX, savedScrollY);
                setOpen(true);

            } else {
                // Desktop: use getDisplayMedia
                const restoreSnackbar = hideShareSnackbar();
                setOpen(false);
                await new Promise<void>((r) => requestAnimationFrame(() => r()));
                try {
                    const shot = await captureScreenshot();
                    if (shot) addScreenshot(shot);
                } finally {
                    restoreSnackbar();
                    setOpen(true);
                }
                return;
            }

            if (!blob) throw new Error('Capture failed');

            addScreenshot({
                id: crypto.randomUUID(),
                blob,
                localUrl: URL.createObjectURL(blob),
                type: 'image',
                mimeType: blob.type,
            });
        } catch (err) {
            console.error('[feedback] screenshot capture failed:', err);
            const detail = err instanceof Error ? err.message : String(err);
            setServerError(`Screenshot failed: ${detail}`);
            setOpen(true); // always reopen on error
        }
    }

    const submitLabel =
        submitStage === 'uploading'
            ? `Sending feedback with ${[
                media.screenshots.length > 0 &&
                `${media.screenshots.length} screenshot${media.screenshots.length > 1 ? 's' : ''}`,
                media.recording && 'recording',
            ].filter(Boolean).join(' & ')}…`
            : submitStage === 'saving'
                ? 'Saving…'
                : 'Submit';

    const modalWidth = isSmallMobile ? 'w-[300px]' : isMobile ? 'w-[320px]' : 'w-[380px]';

    return (
        <>
            {isMobile || isSmallMobile ? (
                <button
                    ref={btnRef}
                    data-feedback-widget="true"
                    onClick={toggleWidget}
                    className="
                        fixed z-[9999] px-2 py-1 text-xs font-semibold
                        text-white bg-primary rounded-lg shadow-lg touch-none
                        select-none cursor-grab active:cursor-grabbing
                    "
                    style={{
                        visibility: btnPos ? 'visible' : 'hidden',
                        top: btnPos && btnPos?.top > 0 ? btnPos.top : 0,
                        left: btnPos?.left ?? 0,
                    }}
                >
                    Feedback
                </button>
            ) : (
                /* Desktop: fixed vertical tab on the right edge */
                <FilledButton
                    onClick={toggleWidget}
                    className="fixed -right-10 bottom-32 z-[9999] shadow-lg writing-mode-vertical-rl -rotate-90"
                >
                    Feedback
                </FilledButton>
            )}

            {/* Backdrop */}
            {open && !modalHidden && (
                <div
                    className="fixed inset-0 z-[9998] bg-black/50"
                    onClick={handleCloseForRecording}
                />
            )}

            {/* Modal */}
            {open && (
                <div
                    className={`
                        fixed z-[9999] bg-white rounded-xl shadow-xl border-t
                        animate-[fadeIn_.2s_ease] transition-opacity max-h-[85vh] overflow-y-auto
                        ${modalWidth}
                        ${isMobile ? 'left-1/2 top-10 -translate-x-1/2' : 'right-2 bottom-8'}
                    `}
                    style={{
                        opacity: modalHidden ? 0 : 1,
                        pointerEvents: modalHidden ? 'none' : 'auto',
                    }}
                >
                            <div
                                className="flex items-center justify-between px-4 py-3 cursor-grab active:cursor-grabbing select-none touch-none rounded-t-xl"
                            >
                                <h2 className="flex-1 text-base text-center font-semibold pointer-events-none">
                                    Submit your Feedback
                                </h2>
                                <button
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={handleClose}
                                    className="p-1 rounded hover:bg-gray-100 shrink-0"
                                >
                                    <Close className="h-[1rem] w-[1rem]" />
                                </button>
                            </div>

                            <form
                                onSubmit={handleSubmit(onSubmit)}
                                className="flex flex-col gap-3 px-4 pb-4"
                            >
                                {/* Title */}
                                <Controller name="title" control={control} render={({ field }) => (
                                    <div className="flex flex-col gap-1">
                                        <label className="text-[0.875rem] text-primary-gray" htmlFor="title">
                                            Title<span className="text-red ml-1">*</span>
                                        </label>
                                        <input
                                            {...field}
                                            ref={firstInputRef}
                                            id="title"
                                            data-testid="feedback-title-id"
                                            placeholder="Enter issue subject/title"
                                            className="rounded-lg bg-secondary-white px-4 py-3 text-[0.875rem] text-gray-500 outline-none text-ellipsis"
                                        />
                                        {errors.title?.message && (
                                            <p className="text-[0.75rem] text-red">{errors.title.message}</p>
                                        )}
                                    </div>
                                )} />

                                {/* Description */}
                                <Controller name="description" control={control} render={({ field }) => (
                                    <div className="flex flex-col gap-1">
                                        <label className="text-[0.875rem] text-primary-gray" htmlFor="description">
                                            Noticed an issue?<span className="text-red ml-1">*</span>
                                        </label>
                                        <textarea
                                            {...field}
                                            id="description"
                                            data-testid="feedback-description-id"
                                            rows={3}
                                            placeholder="Enter issue description..."
                                            className="rounded-lg bg-secondary-white px-4 py-3 text-[0.875rem] text-gray-500 outline-none resize-none"
                                        />
                                        {errors.description?.message && (
                                            <p className="text-[0.75rem] text-red">{errors.description.message}</p>
                                        )}
                                    </div>
                                )} />

                                {/* Email */}
                                <Controller name="email" control={control} render={({ field }) => (
                                    <div className="flex flex-col gap-1">
                                        <label className="text-[0.875rem] text-primary-gray" htmlFor="email">
                                            Email<span className="text-red ml-1">*</span>
                                        </label>
                                        <input
                                            {...field}
                                            type="text"
                                            id="email"
                                            data-testid="feedback-email-id"
                                            placeholder="Enter your email"
                                            className="rounded-lg bg-secondary-white px-4 py-3 text-[0.875rem] text-gray-500 outline-none text-ellipsis"
                                        />
                                        {errors.email?.message && (
                                            <p className="text-[0.75rem] text-red">{errors.email.message}</p>
                                        )}
                                    </div>
                                )} />

                                {/* Media capture */}
                                <div className="flex flex-col gap-2">
                                    <p className="text-[0.875rem] text-primary-gray">
                                        Attach Screenshots {!(isMobile || isSmallMobile) ? 'or Recording' : ''}
                                    </p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <IconBtn
                                            onClick={handleCaptureScreenshot}
                                            title="Add screenshot"
                                            disabled={submitting || isCapturing}
                                        >
                                            {isMobile || isSmallMobile ?
                                                <Camera className="text-primary" /> :
                                                <ScreenshotIcon className="text-primary" />
                                            }
                                        </IconBtn>

                                        {isRecording ? (
                                            <IconBtn
                                                onClick={stopRecording}
                                                title="Stop recording"
                                                className="bg-red-100 border-red-400 text-red-600"
                                                disabled={submitting}
                                            >
                                                <StopIcon />
                                            </IconBtn>
                                        ) : (
                                            (!media.recording && !isMobile) && (
                                                <IconBtn
                                                    onClick={startRecording}
                                                    title="Record screen"
                                                    disabled={submitting || isCapturing}
                                                >
                                                    <VideoIcon className="text-primary" />
                                                </IconBtn>
                                            )
                                        )}

                                        {media.screenshots.length > 0 && (
                                            <span className="text-xs text-gray-500">
                                                {media.screenshots.length} screenshot
                                                {media.screenshots.length > 1 ? 's' : ''}
                                            </span>
                                        )}

                                        {isRecording && (
                                            <span className="flex items-center gap-1 text-xs text-red-500">
                                                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                                                Recording…
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Screenshots grid */}
                                {media.screenshots.length > 0 && (
                                    <div className="grid grid-cols-2 gap-2">
                                        {media.screenshots.map((shot, i) => (
                                            <ScreenshotThumb
                                                key={shot.id}
                                                shot={shot}
                                                index={i}
                                                disabled={submitting}
                                                onRemove={() => removeScreenshot(shot.id)}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Recording preview */}
                                {media.recording && (
                                    <div className="relative rounded border bg-gray-50 p-1">
                                        <span className="absolute top-1 left-2 text-[10px] font-medium text-gray-500 bg-white/80 px-1 rounded z-10">
                                            Recording
                                        </span>
                                        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                                        <video
                                            src={media.recording.localUrl}
                                            className="max-h-24 w-full rounded"
                                            controls
                                        />
                                        {!submitting && (
                                            <button
                                                type="button"
                                                onClick={resetRecording}
                                                className="absolute top-1 right-1 w-5 h-5 text-xs text-white bg-black/60 rounded-full flex items-center justify-center z-10 hover:bg-black/80"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                )}

                                {(mediaError ?? serverError) && (
                                    <InlineAlert status="error">{mediaError ?? serverError}</InlineAlert>
                                )}

                                <div className="flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={submitting || isRecording}
                                        className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 min-w-[90px] text-center"
                                    >
                                        {submitting ? (
                                            <span className="flex items-center justify-center gap-1.5">
                                                <Spinner />
                                                {submitLabel}
                                            </span>
                                        ) : 'Submit'}
                                    </button>
                                </div>
                            </form>
                </div>
            )}
        </>
    );
}

/* ── ScreenshotThumb ──────────────────────────────────────────────────────── */
function ScreenshotThumb({ shot, index, disabled, onRemove }: {
    shot: CapturedMedia; index: number; disabled: boolean; onRemove: () => void;
}) {
    return (
        <div className="relative group rounded border bg-gray-50 overflow-hidden">
            <Image
                src={shot.localUrl}
                alt={`Screenshot ${index + 1}`}
                className="w-full h-20 object-cover"
                width={0} height={0}
                unoptimized
            />
            {!disabled && (
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                        type="button"
                        onClick={onRemove}
                        className="px-2 py-1 text-xs bg-red-500 text-white rounded font-medium hover:bg-red-600"
                    >
                        ✕ Remove
                    </button>
                </div>
            )}
            <span className="absolute top-1 left-1 text-[9px] font-semibold bg-black/50 text-white px-1 rounded">
                #{index + 1}
            </span>
        </div>
    );
}

/* ── IconBtn ──────────────────────────────────────────────────────────────── */
function IconBtn({ children, className = '', title, disabled, onClick }: {
    children: React.ReactNode; className?: string; title?: string;
    disabled?: boolean; onClick?: () => void;
}) {
    return (
        <button
            type="button" title={title} onClick={onClick} disabled={disabled}
            className={`w-9 h-9 flex items-center justify-center bg-gray-100 border rounded hover:bg-blue-50 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
        >
            {children}
        </button>
    );
}
